import { z } from 'zod'
import { AgentProvenanceEntrySchema } from './agent_provenance_entry.ts'
import { CompanyTeamEntrySchema } from './company_team_entry.ts'
import { TaskRecordSchema } from './task_record.ts'
import { DealCommitmentEntrySchema } from './deal_commitment_entry.ts'
import { UserEntrySchema } from './user_entry.ts'
import { makeFieldKeys, validateWithZodSchema } from '../../core/utils/utilities.ts'
import { DealsModelFields } from './deals_model.ts'
import { PipelineDealsModelFields } from './pipeline_deals_model.ts'
import { AccountsModelFields } from './accounts_model.ts'
import { PipelineStagesModelFields } from './pipeline_stages_model.ts'
import { PipelineConfigModelFields } from './pipeline_config_model.ts'
import { RoundsModelFields } from './rounds_model.ts'
import {
  TABLE_DEALS,
  TABLE_ACCOUNTS,
  TABLE_PIPELINE_DEALS,
  TABLE_PIPELINE_CONFIG,
  TABLE_PIPELINE_STAGES,
  TABLE_ROUNDS,
  TABLE_CONTACT_ACCOUNTS,
  TABLE_CONTACT_DEALS,
  FIELD_KEY_SEPARATOR,
  FIELD_KEY_PROG,
  FIELD_KEY_LABEL
} from '../globals.ts'

const ALIAS_DEALS = `${TABLE_DEALS}${FIELD_KEY_SEPARATOR}` as const
const ALIAS_PIPELINE_DEALS = `${TABLE_PIPELINE_DEALS}${FIELD_KEY_SEPARATOR}` as const
const ALIAS_PIPELINE_CONFIG = `${TABLE_PIPELINE_CONFIG}${FIELD_KEY_SEPARATOR}` as const
const ALIAS_ACCOUNTS = `${TABLE_ACCOUNTS}${FIELD_KEY_SEPARATOR}` as const
const ALIAS_PIPELINE_STAGES = `${TABLE_PIPELINE_STAGES}${FIELD_KEY_SEPARATOR}` as const
const ALIAS_ROUNDS = `${TABLE_ROUNDS}${FIELD_KEY_SEPARATOR}` as const
const ALIAS_CONTACT_ACCOUNTS = `${TABLE_CONTACT_ACCOUNTS}${FIELD_KEY_SEPARATOR}` as const
const ALIAS_CONTACT_DEALS = `${TABLE_CONTACT_DEALS}${FIELD_KEY_SEPARATOR}` as const

const PROG_COMPANY_TEAM = `${FIELD_KEY_PROG}company_team` as const
const PROG_TASKS = `${FIELD_KEY_PROG}tasks` as const
const PROG_DEAL_COMMITMENTS = `${FIELD_KEY_PROG}deal_commitments` as const
const FIELD_CONTACT_ACCOUNTS_INTRODUCED_BY = `${ALIAS_CONTACT_ACCOUNTS}introduced_by` as const
const FIELD_CONTACT_ACCOUNTS_SOURCED_BY = `${ALIAS_CONTACT_ACCOUNTS}sourced_by` as const
const FIELD_CONTACT_DEALS_LEAD = `${ALIAS_CONTACT_DEALS}lead` as const

const PROG_COMMITMENT_AMOUNT = `${FIELD_KEY_PROG}commitment_amount` as const
const PROG_VALUATION = `${FIELD_KEY_PROG}valuation` as const
const PROG_VALUATION_TYPE = `${FIELD_KEY_PROG}valuation_type` as const
const PROG_IS_NEW_ACCOUNT = `${FIELD_KEY_PROG}is_new_account` as const
const FIELD_DEAL_ID = `${ALIAS_DEALS}${DealsModelFields.id}` as const
const FIELD_DEAL_NAME = `${ALIAS_DEALS}${DealsModelFields.name}` as const
const FIELD_DEAL_TYPE = `${ALIAS_DEALS}${DealsModelFields.deal_type}` as const
const FIELD_DEAL_PIPELINE_ORIGINATION_ID = `${ALIAS_DEALS}${DealsModelFields.pipeline_origination}` as const
const FIELD_DEAL_PIPELINE_EXECUTION_ID = `${ALIAS_DEALS}${DealsModelFields.pipeline_execution}` as const
const FIELD_DEAL_ALLOCATION_AVAILABLE = `${ALIAS_DEALS}${DealsModelFields.allocation_available}` as const
const FIELD_DEAL_EXCHANGE_RATE = `${ALIAS_DEALS}${DealsModelFields.exchange_rate}` as const
const FIELD_DEAL_CURRENCY = `${ALIAS_DEALS}${DealsModelFields.currency}` as const
const FIELD_DEAL_ACCOUNT_ID = `${ALIAS_DEALS}${DealsModelFields.account}` as const

const FIELD_PIPELINE_DEALS_STAGE = `${ALIAS_PIPELINE_DEALS}${PipelineDealsModelFields.stage}` as const
const FIELD_PIPELINE_DEALS_STAGE_DATE = `${ALIAS_PIPELINE_DEALS}${PipelineDealsModelFields.stage_date}` as const
const FIELD_PIPELINE_DEALS_DATE_CREATED = `${ALIAS_PIPELINE_DEALS}${PipelineDealsModelFields.date_created}` as const
const FIELD_PIPELINE_DEALS_SEQUENCE = `${ALIAS_PIPELINE_DEALS}${PipelineDealsModelFields.sequence}` as const
const FIELD_PIPELINE_DEALS_ID = `${ALIAS_PIPELINE_DEALS}${PipelineDealsModelFields.id}` as const
const FIELD_PIPELINE_CONFIG_ID = `${ALIAS_PIPELINE_CONFIG}${PipelineConfigModelFields.id}` as const

const FIELD_ACC_ID = `${ALIAS_ACCOUNTS}${AccountsModelFields.id}` as const
const FIELD_ACC_NAME = `${ALIAS_ACCOUNTS}${AccountsModelFields.name}` as const
const FIELD_ACC_LEGAL_NAME = `${ALIAS_ACCOUNTS}${AccountsModelFields.legal_name}` as const
const FIELD_ACC_DESCRIPTION = `${ALIAS_ACCOUNTS}${AccountsModelFields.description}` as const
const FIELD_ACC_WEBSITE = `${ALIAS_ACCOUNTS}${AccountsModelFields.website}` as const
const FIELD_ACC_INDUSTRY = `${ALIAS_ACCOUNTS}${AccountsModelFields.industry}` as const
const FIELD_ACC_CRITICAL_TECHNOLOGY_AREA = `${ALIAS_ACCOUNTS}${AccountsModelFields.critical_technology_area}` as const
const FIELD_ACC_BUSINESS_CITY_CODE = `${ALIAS_ACCOUNTS}${AccountsModelFields.business_city_code}` as const
const FIELD_ACC_BUSINESS_CITY_LABEL = `${ALIAS_ACCOUNTS}${AccountsModelFields.business_city_label}` as const
const FIELD_ACC_BUSINESS_COUNTRY_CODE = `${ALIAS_ACCOUNTS}${AccountsModelFields.business_country_code}` as const
const FIELD_ACC_BUSINESS_COUNTRY_LABEL = `${ALIAS_ACCOUNTS}${AccountsModelFields.business_country_label}` as const
const FIELD_ACC_BUSINESS_ADDRESS_1 = `${ALIAS_ACCOUNTS}${AccountsModelFields.business_address_1}` as const
const FIELD_ACC_BUSINESS_ADDRESS_2 = `${ALIAS_ACCOUNTS}${AccountsModelFields.business_address_2}` as const
const FIELD_ACC_BUSINESS_STATEPROVINCE = `${ALIAS_ACCOUNTS}${AccountsModelFields.business_stateprovince}` as const
const FIELD_ACC_BUSINESS_POSTALCODE = `${ALIAS_ACCOUNTS}${AccountsModelFields.business_postalcode}` as const
const FIELD_ACC_OFFICE_CITY_CODE = `${ALIAS_ACCOUNTS}${AccountsModelFields.office_city_code}` as const
const FIELD_ACC_OFFICE_COUNTRY_CODE = `${ALIAS_ACCOUNTS}${AccountsModelFields.office_country_code}` as const
const FIELD_ACC_OFFICE_ADDRESS_1 = `${ALIAS_ACCOUNTS}${AccountsModelFields.office_address_1}` as const
const FIELD_ACC_OFFICE_ADDRESS_2 = `${ALIAS_ACCOUNTS}${AccountsModelFields.office_address_2}` as const
const FIELD_ACC_OFFICE_STATEPROVINCE = `${ALIAS_ACCOUNTS}${AccountsModelFields.office_stateprovince}` as const
const FIELD_ACC_OFFICE_POSTALCODE = `${ALIAS_ACCOUNTS}${AccountsModelFields.office_postalcode}` as const
const FIELD_ACC_SAME_AS_BUSINESS_ADDRESS = `${ALIAS_ACCOUNTS}${AccountsModelFields.same_as_business_address}` as const

const FIELD_PS_TYPE = `${ALIAS_PIPELINE_STAGES}${PipelineStagesModelFields.type}` as const
const FIELD_PS_COLOUR = `${ALIAS_PIPELINE_STAGES}${PipelineStagesModelFields.colour}` as const
const FIELD_PS_SEQUENCE = `${ALIAS_PIPELINE_STAGES}${PipelineStagesModelFields.sequence}` as const
const FIELD_PS_DECISION_STATUS = `${ALIAS_PIPELINE_STAGES}${PipelineStagesModelFields.decision_status}` as const

const FIELD_ROUND_ID = `${ALIAS_ROUNDS}${RoundsModelFields.id}` as const
const FIELD_ROUND_NAME = `${ALIAS_ROUNDS}${RoundsModelFields.name}` as const
const FIELD_ROUND_FUNDING_STAGE = `${ALIAS_ROUNDS}${RoundsModelFields.funding_stage}` as const
const FIELD_ROUND_RAISE_AMOUNT = `${ALIAS_ROUNDS}${RoundsModelFields.raise_amount}` as const
const FIELD_ROUND_PRE_MONEY_VALUATION = `${ALIAS_ROUNDS}${RoundsModelFields.pre_money_valuation}` as const
const FIELD_ROUND_POST_MONEY_VALUATION = `${ALIAS_ROUNDS}${RoundsModelFields.post_money_valuation}` as const
const FIELD_ROUND_SECURITY_TYPE = `${ALIAS_ROUNDS}${RoundsModelFields.security_type}` as const
const FIELD_ROUND_ROUND_CLOSE_DATE = `${ALIAS_ROUNDS}${RoundsModelFields.round_close_date}` as const
const FIELD_ROUND_IS_FUNDING_ROUND = `${ALIAS_ROUNDS}${DealsModelFields.is_funding_round}` as const

// Agent Access 4.5: null unless an agent session created this record.
const PROG_CREATED_PROVENANCE = `${FIELD_KEY_PROG}created_provenance` as const

// Record provenance shown in the shared record chrome (drawer banner +
// full-form tab bar). Literals rather than DealsModelFields lookups: that
// schema governs WRITES and neither field is writable — date_created is
// trigger-maintained and created_by_name is computed by the RPC from
// audit.resolve_user_name, which yields 'System' for a null created_by.
// Populated in every read mode — the drawer has no by-id read and renders
// the row the grid returned.
const FIELD_DEAL_DATE_CREATED = `${ALIAS_DEALS}date_created` as const
const FIELD_DEAL_CREATED_BY_NAME = `${ALIAS_DEALS}created_by_name` as const

export const DealRecordSchema = z
  .object({
    [FIELD_DEAL_ID]: z.string().nullish(),
    [FIELD_DEAL_NAME]: z.string().nullish(),
    [FIELD_DEAL_TYPE]: z.string().nullish(),
    [FIELD_DEAL_PIPELINE_ORIGINATION_ID]: z.string().nullish(),
    [FIELD_DEAL_PIPELINE_EXECUTION_ID]: z.string().nullish(),
    [FIELD_PIPELINE_DEALS_STAGE]: z.string().nullish(),
    [FIELD_PIPELINE_DEALS_STAGE_DATE]: z.string().nullish(),
    [FIELD_PIPELINE_DEALS_DATE_CREATED]: z.string().nullish(),
    [FIELD_PIPELINE_DEALS_SEQUENCE]: z.number().nullish(),
    [FIELD_PIPELINE_DEALS_ID]: z.string().nullish(),
    [FIELD_PIPELINE_CONFIG_ID]: z.string().nullish(),
    [FIELD_DEAL_ALLOCATION_AVAILABLE]: z.number().nullish(),
    [FIELD_DEAL_EXCHANGE_RATE]: z.number().nullish(),
    [FIELD_DEAL_CURRENCY]: z.string().nullish(),
    [FIELD_DEAL_ACCOUNT_ID]: z.string().nullish(),

    [FIELD_ACC_ID]: z.string().nullish(),
    [FIELD_ACC_NAME]: z.string().nullish(),
    [FIELD_ACC_LEGAL_NAME]: z.string().nullish(),
    [FIELD_ACC_DESCRIPTION]: z.string().nullish(),
    [FIELD_ACC_WEBSITE]: z.string().nullish(),
    [FIELD_ACC_INDUSTRY]: z.string().nullish(),
    [FIELD_ACC_CRITICAL_TECHNOLOGY_AREA]: z.string().nullish(),
    [FIELD_ACC_BUSINESS_CITY_CODE]: z.number().nullish(),
    [FIELD_ACC_BUSINESS_COUNTRY_CODE]: z.string().nullish(),
    [FIELD_ACC_BUSINESS_ADDRESS_1]: z.string().nullish(),
    [FIELD_ACC_BUSINESS_ADDRESS_2]: z.string().nullish(),
    [FIELD_ACC_BUSINESS_STATEPROVINCE]: z.string().nullish(),
    [FIELD_ACC_BUSINESS_POSTALCODE]: z.string().nullish(),
    [FIELD_ACC_OFFICE_CITY_CODE]: z.number().nullish(),
    [FIELD_ACC_OFFICE_COUNTRY_CODE]: z.string().nullish(),
    [FIELD_ACC_OFFICE_ADDRESS_1]: z.string().nullish(),
    [FIELD_ACC_OFFICE_ADDRESS_2]: z.string().nullish(),
    [FIELD_ACC_OFFICE_STATEPROVINCE]: z.string().nullish(),
    [FIELD_ACC_OFFICE_POSTALCODE]: z.string().nullish(),
    [FIELD_ACC_SAME_AS_BUSINESS_ADDRESS]: z.boolean().nullish(),

    [FIELD_PS_TYPE]: z.string().nullish(),
    [FIELD_PS_COLOUR]: z.string().nullish(),
    [FIELD_PS_SEQUENCE]: z.number().nullish(),
    [FIELD_PS_DECISION_STATUS]: z.string().nullish(),

    [FIELD_ROUND_ID]: z.string().nullish(),
    [FIELD_ROUND_NAME]: z.string().nullish(),
    [FIELD_ROUND_FUNDING_STAGE]: z.string().nullish(),
    [FIELD_ROUND_RAISE_AMOUNT]: z.number().nullish(),
    [FIELD_ROUND_PRE_MONEY_VALUATION]: z.number().nullish(),
    [FIELD_ROUND_POST_MONEY_VALUATION]: z.number().nullish(),
    [FIELD_ROUND_SECURITY_TYPE]: z.string().nullish(),
    [FIELD_ROUND_ROUND_CLOSE_DATE]: z.string().nullish(),
    [FIELD_ROUND_IS_FUNDING_ROUND]: z.boolean().nullish(),

    [FIELD_ACC_BUSINESS_CITY_LABEL]: z.string().nullish(),
    [`${FIELD_ACC_OFFICE_CITY_CODE}${FIELD_KEY_LABEL}` as const]: z.string().nullish(),
    [FIELD_ACC_BUSINESS_COUNTRY_LABEL]: z.string().nullish(),
    [`${FIELD_ACC_OFFICE_COUNTRY_CODE}${FIELD_KEY_LABEL}` as const]: z.string().nullish(),
    [`${FIELD_ROUND_FUNDING_STAGE}${FIELD_KEY_LABEL}` as const]: z.string().nullish(),
    [`${FIELD_ROUND_SECURITY_TYPE}${FIELD_KEY_LABEL}` as const]: z.string().nullish(),
    [`${FIELD_PS_DECISION_STATUS}${FIELD_KEY_LABEL}` as const]: z.string().nullish(),
    [`${FIELD_PS_TYPE}${FIELD_KEY_LABEL}` as const]: z.string().nullish(),
    [`${FIELD_ACC_INDUSTRY}${FIELD_KEY_LABEL}` as const]: z.string().nullish(),
    [`${FIELD_ACC_CRITICAL_TECHNOLOGY_AREA}${FIELD_KEY_LABEL}` as const]: z.string().nullish(),
    [`${FIELD_DEAL_TYPE}${FIELD_KEY_LABEL}` as const]: z.string().nullish(),
    [`${FIELD_DEAL_CURRENCY}${FIELD_KEY_LABEL}` as const]: z.string().nullish(),
    [`${FIELD_PIPELINE_DEALS_STAGE}${FIELD_KEY_LABEL}` as const]: z.string().nullish(),
    [`${FIELD_PIPELINE_DEALS_STAGE_DATE}${FIELD_KEY_LABEL}` as const]: z.string().nullish(),
    [`${FIELD_PIPELINE_DEALS_SEQUENCE}${FIELD_KEY_LABEL}` as const]: z.number().nullish(),
    [`${FIELD_PIPELINE_CONFIG_ID}${FIELD_KEY_LABEL}` as const]: z.string().nullish(),

    [PROG_COMMITMENT_AMOUNT]: z.number().nullish(),
    [PROG_VALUATION]: z.number().nullish(),
    [PROG_VALUATION_TYPE]: z.string().nullish(),
    [PROG_IS_NEW_ACCOUNT]: z.boolean().nullish(),
    [PROG_COMPANY_TEAM]: z.array(CompanyTeamEntrySchema).nullish(),
    [PROG_TASKS]: z.array(TaskRecordSchema).nullish(),
    [PROG_DEAL_COMMITMENTS]: z.array(DealCommitmentEntrySchema).nullish(),
    [FIELD_CONTACT_ACCOUNTS_INTRODUCED_BY]: z.array(UserEntrySchema).nullish(), // TODO: needs to change to prog
    [FIELD_CONTACT_ACCOUNTS_SOURCED_BY]: z.array(UserEntrySchema).nullish(), // TODO: needs to change to prog
    [FIELD_CONTACT_DEALS_LEAD]: z.array(UserEntrySchema).nullish(), // TODO: needs to change to prog
    [PROG_CREATED_PROVENANCE]: AgentProvenanceEntrySchema.nullish(),
    [FIELD_DEAL_DATE_CREATED]: z.string().nullish(),
    [FIELD_DEAL_CREATED_BY_NAME]: z.string().nullish()
  })
  .passthrough()

export const DealFields = makeFieldKeys(DealRecordSchema)

export type DealRecord = z.infer<typeof DealRecordSchema>

export function validateDealRecord(data: unknown, options: { isArray: true }): DealRecord[]
export function validateDealRecord(data: unknown, options?: { isArray?: false }): DealRecord
export function validateDealRecord(data: unknown, options?: { isArray?: boolean }): DealRecord | DealRecord[]
export function validateDealRecord(data: unknown, options?: { isArray?: boolean }): DealRecord | DealRecord[] {
  try {
    return validateWithZodSchema(DealRecordSchema, data, 'deal record', options)
  } catch (error) {
    throw new Error(`Deal record validation failed: ${error}`)
  }
}
